/**
 * ===========================================================================================
 *  THE EDITOR'S SEAM — the same shape as ./cabinet-api-source.ts and for the same reason.
 *
 *  One change turns the editor from fixtures to the real API. Nothing in
 *  web/authors/src/routes/articles/** knows which implementation it is talking to, because
 *  everything goes through `EditorApi` and gets it from React context.
 *
 *  IT READS THE SAME FLAG AS THE CABINET'S SEAM, deliberately. `isLiveApiConfigured()` is
 *  `VITE_AUTHORS_API_LIVE === 'true'` (src/config.ts), and a second flag would let
 *  a build point the article list at the network and the editor at fixtures — two halves of one
 *  screen's flow disagreeing about which platform they are on.
 *
 *  WHY IT DEFAULTS TO FIXTURES. With no API behind a build, pointing at the network by default
 *  would mean the editor renders an error on every load - and the default build is the one the
 *  accessibility leg checks. The token comes from the app's one token source unless a caller
 *  passes its own (`features/auth/session/access-token.ts`), exactly as the cabinet's seam does.
 * ===========================================================================================
 */
import { accessToken } from '../../auth/session/access-token';
import { isLiveApiConfigured } from './cabinet-api-source';
import { createFixtureEditorApi } from './fixture-editor-api';
import { createHttpEditorApi } from './http-editor-api';
import type { AccessTokenSource } from './http-cabinet-api';
import type { EditorApi } from './editor-api';

export interface EditorApiSourceOptions {
  /**
   * How the bearer token is obtained. Omitted, it is the app's one token source - the auth client's
   * current session - so this app still has exactly one Firebase client. A test passes its own.
   */
  readonly getAccessToken?: AccessTokenSource;
  /** Force one or the other, for tests and for a screenshot of a state nobody can otherwise reach. */
  readonly force?: 'fixtures' | 'http';
  /** Fixture-only: every read fails at the transport, so the error state is renderable. */
  readonly offline?: boolean;
}

export function createEditorApi(options: EditorApiSourceOptions = {}): EditorApi {
  const useHttp =
    options.force === 'http' || (options.force !== 'fixtures' && isLiveApiConfigured());

  if (!useHttp) {
    return createFixtureEditorApi(
      options.offline === undefined ? {} : { offline: options.offline },
    );
  }

  // BEARER-ONLY WITH NO COOKIES across origins (adr/0002): there is no ambient credential, so the
  // token source is defaulted rather than skipped. The editor route calls this with no arguments
  // (`routes/articles/EditorRoute.tsx`, `ArticleEditorScreen`), which used to throw on a live build.
  return createHttpEditorApi({ getAccessToken: options.getAccessToken ?? accessToken });
}
